import { useEffect } from "react";
import { Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { enqueueNotification } from "../../redux/slice/notification/notificationSlice";
import { RootState } from "../../redux/store";
import LoaderOverlay from "../../modules/layout/components/LoaderOverlay";
import routes from "../routes";

/**
 * Rendered as the route element when the active tenant's `services` allow-list
 * does not include the service required by the route.
 *
 * Waits for `configLoaded === true` before deciding to redirect: the route
 * element decision in PageRoutes is based on `state.tenantConfig.config` which
 * starts as `DefaultConfig` until the async tenant load resolves. Without this
 * guard, a direct-paste URL (new tab, no traversal of home/Toolbar) would
 * always see the default services list and redirect, regardless of which
 * tenant the user is actually on. Once loadTenantConfig resolves, the parent
 * route re-evaluates its element prop and either keeps this component (and
 * lets it redirect) or swaps to the real dialog.
 */
const UnavailableServiceRedirect = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const configLoaded = useSelector(
    (state: RootState) => state.tenantConfig.configLoaded
  );

  useEffect(() => {
    if (!configLoaded) return;
    dispatch(
      enqueueNotification({
        severity: "warning",
        message: t("common.service_not_available_here"),
      })
    );
  }, [configLoaded, dispatch, t]);

  if (!configLoaded) {
    return <LoaderOverlay open={true} />;
  }

  return <Navigate to={routes.home} replace />;
};

export default UnavailableServiceRedirect;
